import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth';
import { useMyPendingWalkthroughs, walkthroughKeys } from '@/hooks/walkthrough';
import {
  walkthroughService,
  type Walkthrough,
  type WalkthroughStep,
} from '@/services/walkthrough.service';
import { resolveSelector } from './anchors';
import { onWalkthroughTrigger } from './walkthroughBus';
import { useWalkthroughTarget } from './useWalkthroughTarget';
import { WalkthroughSpotlight } from './WalkthroughSpotlight';
import { WalkthroughCard } from './WalkthroughCard';

/** Deixa o layout assentar antes de medir o primeiro alvo. */
const INITIAL_DELAY_MS = 1000;
/** Espera após navegar para outra rota antes de procurar o alvo. */
const ROUTE_SETTLE_MS = 400;

/**
 * O tour é exclusivo da área do aluno. O painel admin fica de fora — inclusive
 * o editor de Aparência (`/admin/courses/:id/appearance`), que renderiza o
 * StudentLayout em `editMode` e teria as mesmas âncoras no DOM.
 */
function isStudentArea(pathname: string): boolean {
  return !pathname.startsWith('/admin');
}

function stepMatchesRoute(step: WalkthroughStep, pathname: string): boolean {
  if (!step.route) return true;
  return pathname === step.route || pathname.startsWith(`${step.route}/`);
}

/**
 * Tour guiado do aluno: overlay escurecido, spotlight no elemento alvo e card
 * explicativo. Montado globalmente (fora das rotas) para sobreviver à
 * navegação entre páginas.
 */
export function WalkthroughTour() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const inStudentArea = isStudentArea(location.pathname);
  const { data: pending } = useMyPendingWalkthroughs(
    isAuthenticated && inStudentArea,
  );

  const [active, setActive] = useState<Walkthrough | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [ready, setReady] = useState(false);
  const [navigating, setNavigating] = useState(false);
  /** Tours dispensados nesta sessão, para não reabrirem antes do refetch. */
  const dismissedRef = useRef<Set<string>>(new Set());

  const steps = useMemo(() => active?.steps ?? [], [active]);
  const step = steps[stepIndex];

  // ---- Abertura automática ----
  useEffect(() => {
    if (!isAuthenticated || !inStudentArea || active || !pending?.length)
      return;

    const next = pending.find(
      (w) => !dismissedRef.current.has(w.id) && (w.steps?.length ?? 0) > 0,
    );
    if (!next) return;

    const timer = setTimeout(() => {
      setActive(next);
      // Retoma de onde parou se já havia progresso salvo
      const saved = next.userProgress?.currentStep ?? 0;
      const total = next.steps?.length ?? 0;
      setStepIndex(Math.min(Math.max(saved, 0), Math.max(total - 1, 0)));
      setReady(true);
    }, INITIAL_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isAuthenticated, inStudentArea, active, pending]);

  // ---- Abertura manual (botão "rever tutorial") ----
  useEffect(() => {
    return onWalkthroughTrigger(async (context) => {
      try {
        const target = context.walkthroughId
          ? await walkthroughService.getPublishedById(context.walkthroughId)
          : ((await walkthroughService.getMyPending())[0] ?? null);

        if (!target || !(target.steps?.length ?? 0)) return;

        dismissedRef.current.delete(target.id);
        setActive(target);
        setStepIndex(0);
        setReady(true);
      } catch {
        // Tour indisponível: silencioso, não é ação crítica do usuário
      }
    });
  }, []);

  // ---- Navegação entre rotas dos passos ----
  useEffect(() => {
    if (!active || !step) return;
    if (stepMatchesRoute(step, location.pathname)) {
      setNavigating(false);
      return;
    }

    setNavigating(true);
    navigate(step.route as string);
  }, [active, step, location.pathname, navigate]);

  // Depois de chegar na rota certa, espera o layout assentar
  useEffect(() => {
    if (!navigating) return;
    if (!step || !stepMatchesRoute(step, location.pathname)) return;

    const timer = setTimeout(() => setNavigating(false), ROUTE_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [navigating, step, location.pathname]);

  const selector = step ? resolveSelector(step) : null;
  const stepConfig = step?.config ?? {};

  const { rect, status } = useWalkthroughTarget({
    selector: step?.type === 'modal' ? null : selector,
    waitForSelectorMs: stepConfig.waitForSelectorMs,
    scrollIntoView: stepConfig.scrollIntoView !== false,
    paused: !ready || navigating || !step,
  });

  // ---- Ações ----

  const close = useCallback(() => {
    setActive(null);
    setStepIndex(0);
    setReady(false);
    setNavigating(false);
  }, []);

  const handleSkip = useCallback(async () => {
    if (!active) return;
    const id = active.id;
    dismissedRef.current.add(id);
    close();
    try {
      await walkthroughService.skip(id);
      qc.invalidateQueries({ queryKey: walkthroughKeys.myPending() });
    } catch {
      // Falha ao persistir não deve reabrir o tour na cara do usuário
    }
  }, [active, close, qc]);

  const handleComplete = useCallback(async () => {
    if (!active) return;
    const id = active.id;
    dismissedRef.current.add(id);
    close();
    try {
      await walkthroughService.complete(id);
      qc.invalidateQueries({ queryKey: walkthroughKeys.myPending() });
    } catch {
      // idem
    }
  }, [active, close, qc]);

  const handleNext = useCallback(() => {
    if (!active) return;
    const next = stepIndex + 1;

    if (next >= steps.length) {
      void handleComplete();
      return;
    }

    setStepIndex(next);
    void walkthroughService.saveProgress(active.id, next).catch(() => {
      // Progresso é best-effort; o tour continua
    });
  }, [active, stepIndex, steps.length, handleComplete]);

  const handlePrev = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  // ---- Render ----

  if (!ready || !active || !step || !inStudentArea) return null;

  // Só destaca depois de medir o alvo. Enquanto procura (ou navega entre
  // rotas) o overlay continua no ar sem recorte e o card fica centralizado —
  // assim o tour nunca some da tela. Se o alvo não aparecer dentro do tempo,
  // o passo permanece como modal centralizado em vez de travar.
  const effectiveRect = status === 'found' && !navigating ? rect : null;

  return createPortal(
    <AnimatePresence>
      <WalkthroughSpotlight
        key={`spotlight-${step.id}`}
        rect={effectiveRect}
        padding={step.padding}
      />
      <WalkthroughCard
        key={`card-${step.id}`}
        step={step}
        rect={effectiveRect}
        stepIndex={stepIndex}
        totalSteps={steps.length}
        showProgress={active.showProgress}
        allowSkip={active.allowSkip}
        onPrev={handlePrev}
        onNext={handleNext}
        onSkip={handleSkip}
      />
    </AnimatePresence>,
    document.body,
  );
}
